import { useEffect, useRef, useState } from "react";
import Hls from "hls.js";
import {
  AlertTriangleIcon,
  CalendarRangeIcon,
  CheckCircle2Icon,
  CloudIcon,
  GridIcon,
  ServerIcon,
  StepBackIcon,
  StepForwardIcon,
  XIcon,
} from "lucide-react";

import { useCameras } from "@/api/cameras";
import {
  useNvrClips,
  usePlayRecording,
  useRecordings,
  useRequestNvrClip,
} from "@/api/recordings";
import {
  useAcknowledgeGap,
  useCameraTimeline,
  useRecordingGaps,
} from "@/api/playback";
import { ComplianceDashboard } from "@/components/ComplianceDashboard";
import { MultiCameraPlayback } from "@/components/MultiCameraPlayback";
import { RecordingScheduleEditor } from "@/components/RecordingScheduleEditor";
import { TimelineScrubber, type ScrubberGap } from "@/components/TimelineScrubber";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDate, cn } from "@/lib/utils";
import type { NvrClip, NvrClipStatus, Recording } from "@/types";


type Tab = "cloud" | "nvr" | "multi" | "schedule" | "compliance";

const SPEED_OPTIONS = [0.5, 1, 2, 4, 8] as const;
type Speed = typeof SPEED_OPTIONS[number];


function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}


// ===========================================================================
// Cloud Recordings tab
// ===========================================================================
function CloudTab({
  cameraId,
  date,
}: {
  cameraId: string | "";
  date: string;
}) {
  const recordings = useRecordings({
    cameraId: cameraId || undefined,
    date,
    limit: 200,
  });
  const [activeId, setActiveId] = useState<string | null>(null);

  const items = recordings.data?.items ?? [];

  // S8 — pull the per-camera timeline (segments + gaps) so the
  // scrubber can render the red gap markers. Only enabled when a
  // single camera is selected; the timeline endpoint is per-camera.
  const startIso = `${date}T00:00:00Z`;
  const endIso = `${date}T23:59:59Z`;
  const timeline = useCameraTimeline(
    cameraId || null, cameraId ? startIso : null, cameraId ? endIso : null,
  );
  // Convert format_timestamp shape to plain ISO for the scrubber.
  const scrubberGaps: ScrubberGap[] = (timeline.data?.gaps ?? []).map((g) => ({
    start: g.start?.utc ?? "",
    end: g.end?.utc ?? "",
    label: `Gap · ${g.gap_seconds}s`,
  })).filter((g) => g.start && g.end);

  // Gap list for the side panel.
  const gapsForDate = useRecordingGaps({
    cameraId: cameraId || undefined,
    startDate: date,
    endDate: date,
  });
  const ackMutation = useAcknowledgeGap();

  // Auto-select the most recent recording on load / filter change.
  useEffect(() => {
    if (!activeId && items.length > 0) {
      setActiveId(items[0].id);
    } else if (activeId && !items.find((r) => r.id === activeId)) {
      setActiveId(items[0]?.id ?? null);
    }
  }, [activeId, items]);

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">
            Timeline · {date}
            {scrubberGaps.length > 0 && (
              <span className="ml-2 text-xs text-red-500">
                · {scrubberGaps.length} gap{scrubberGaps.length === 1 ? "" : "s"}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {recordings.isLoading ? (
            <p className="text-xs text-muted-foreground">Loading…</p>
          ) : items.length === 0 ? (
            <p className="text-xs text-muted-foreground">
              No recordings for the selected filters.
            </p>
          ) : (
            <TimelineScrubber
              date={date}
              recordings={items}
              activeRecordingId={activeId}
              onSelect={(r) => setActiveId(r.id)}
              gaps={scrubberGaps}
            />
          )}
        </CardContent>
      </Card>

      {cameraId && (gapsForDate.data?.length ?? 0) > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-sm">
              <AlertTriangleIcon className="h-3.5 w-3.5 text-red-500" />
              Gaps for this camera · {date}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left">Start</th>
                  <th className="px-3 py-2 text-left">End</th>
                  <th className="px-3 py-2 text-right">Duration</th>
                  <th className="px-3 py-2 text-left">Acknowledged</th>
                  <th className="px-3 py-2 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {(gapsForDate.data ?? []).map((g) => (
                  <tr key={g.id} className="border-b last:border-0">
                    <td className="px-3 py-2 text-xs">
                      {g.gap_start?.local?.slice(11, 16) ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {g.gap_end?.local?.slice(11, 16) ?? "—"}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {g.gap_seconds < 60
                        ? `${g.gap_seconds}s`
                        : `${(g.gap_seconds / 60).toFixed(1)}m`}
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {g.acknowledged ? (
                        <span className="inline-flex items-center gap-1 text-emerald-600">
                          <CheckCircle2Icon className="h-3 w-3" /> Yes
                        </span>
                      ) : (
                        <span className="text-muted-foreground">No</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {!g.acknowledged && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            void ackMutation.mutateAsync({ gapId: g.id })
                          }
                        >
                          Acknowledge
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <RecordingPlayer recordingId={activeId} />

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Clips</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              Nothing yet.
            </p>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Start</th>
                  <th className="px-4 py-2 text-left">End</th>
                  <th className="px-4 py-2 text-left">Duration</th>
                  <th className="px-4 py-2 text-right">Size</th>
                  <th className="px-4 py-2 text-left">Status</th>
                  <th className="px-4 py-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => (
                  <tr
                    key={r.id}
                    className={`border-b last:border-0 ${
                      r.id === activeId ? "bg-[#ff6d00]/5" : ""
                    }`}
                  >
                    <td className="px-4 py-2">{formatDate(r.started_at)}</td>
                    <td className="px-4 py-2">
                      {r.ended_at ? formatDate(r.ended_at) : "—"}
                    </td>
                    <td className="px-4 py-2">{r.duration_sec ?? "—"}s</td>
                    <td className="px-4 py-2 text-right">
                      {r.size_bytes
                        ? `${(r.size_bytes / 1_000_000).toFixed(2)} MB`
                        : "—"}
                    </td>
                    <td className="px-4 py-2">
                      <RecordingStatusBadge status={r.status} />
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setActiveId(r.id)}
                      >
                        Play
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


function RecordingStatusBadge({ status }: { status: Recording["status"] }) {
  const variants: Record<Recording["status"], "online" | "warning" | "error" | "outline"> = {
    recording: "online",
    completed: "outline",
    failed: "error",
    deleted: "outline",
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}


// Reads the playlist from the API, writes the body to a Blob URL, and
// hands that to hls.js. Server-side presigning takes care of S3 auth.
function RecordingPlayer({ recordingId }: { recordingId: string | null }) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  // S8 — speed + frame-step controls.
  const [speed, setSpeed] = useState<Speed>(1);
  const [paused, setPaused] = useState(false);
  const { data, isLoading, isError } = usePlayRecording(
    recordingId ?? undefined,
  );

  useEffect(() => {
    if (!data || !videoRef.current) return;
    const video = videoRef.current;
    const blob = new Blob([data.hls_playlist], {
      type: "application/vnd.apple.mpegurl",
    });
    const url = URL.createObjectURL(blob);

    const cleanup = () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      URL.revokeObjectURL(url);
    };

    const canNative = video.canPlayType("application/vnd.apple.mpegurl") !== "";
    if (canNative) {
      video.src = url;
    } else if (Hls.isSupported()) {
      const hls = new Hls();
      hls.loadSource(url);
      hls.attachMedia(video);
      hlsRef.current = hls;
    }
    return cleanup;
  }, [data]);

  // S8 — propagate speed to the underlying <video> element. hls.js
  // doesn't expose its own speed knob; playbackRate works for HLS
  // playback in every modern browser.
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = speed;
  }, [speed]);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const onPlay = () => setPaused(false);
    const onPause = () => setPaused(true);
    v.addEventListener("play", onPlay);
    v.addEventListener("pause", onPause);
    return () => {
      v.removeEventListener("play", onPlay);
      v.removeEventListener("pause", onPause);
    };
  }, []);

  function stepFrame(direction: 1 | -1) {
    // Frame-step assumption: 25 fps → 40 ms per frame. Only meaningful
    // when paused (otherwise the next paint overwrites the position).
    if (!videoRef.current || !paused) return;
    videoRef.current.currentTime += direction * 0.04;
  }

  if (!recordingId) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Select a clip from the timeline or the table to play.
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm">Player</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-xs text-muted-foreground">Resolving playlist…</p>
        )}
        {isError && (
          <p className="text-xs text-destructive">Could not load playlist.</p>
        )}
        <video
          ref={videoRef}
          controls
          className="aspect-video w-full rounded bg-black"
        />
        {/* S8 — speed + frame-step row */}
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <span className="text-[11px] text-muted-foreground">Speed:</span>
          <div className="inline-flex items-center gap-1 rounded border border-border p-0.5">
            {SPEED_OPTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSpeed(s)}
                className={cn(
                  "rounded px-2 py-0.5 text-[11px] transition",
                  speed === s
                    ? "border-[#ff6d00] bg-[#ff6d00]/15 text-[#ff6d00] border"
                    : "text-muted-foreground hover:bg-muted",
                )}
              >
                {s}×
              </button>
            ))}
          </div>
          {paused && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => stepFrame(-1)}
                title="Step backward 1 frame"
              >
                <StepBackIcon className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => stepFrame(1)}
                title="Step forward 1 frame"
              >
                <StepForwardIcon className="h-3.5 w-3.5" />
              </Button>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}


// ===========================================================================
// NVR Retrieval tab
// ===========================================================================
function NvrTab({ defaultCameraId }: { defaultCameraId: string | "" }) {
  const cameras = useCameras();
  const clips = useNvrClips();
  const request = useRequestNvrClip();
  // camera_id → camera.name, for the Camera column of the queue table.
  const cameraNameById: Record<string, string> = (cameras.data?.items ?? []).reduce(
    (acc, c) => {
      acc[c.id] = c.name;
      return acc;
    },
    {} as Record<string, string>,
  );

  const today = todayIso();
  const [cameraId, setCameraId] = useState(defaultCameraId);
  const [date, setDate] = useState(today);
  const [start, setStart] = useState("10:00");
  const [end, setEnd] = useState("10:05");
  const [openClip, setOpenClip] = useState<NvrClip | null>(null);

  useEffect(() => {
    if (!cameraId && cameras.data?.items?.length) {
      setCameraId(cameras.data.items[0].id);
    }
  }, [cameraId, cameras.data]);

  async function submit() {
    if (!cameraId) return;
    const startIso = new Date(`${date}T${start}:00Z`).toISOString();
    const endIso = new Date(`${date}T${end}:00Z`).toISOString();
    try {
      await request.mutateAsync({
        camera_id: cameraId,
        start_time: startIso,
        end_time: endIso,
      });
    } catch {
      // mutation captures the error; banner below picks it up
    }
  }

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Retrieve footage from NVR</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Pulls historical footage stored on the NVR HDD. Maximum 60-minute
            window per request — longer windows are split into multiple
            retrievals.
          </p>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <div className="space-y-1">
              <Label htmlFor="nvr_camera">Camera</Label>
              <select
                id="nvr_camera"
                value={cameraId}
                onChange={(e) => setCameraId(e.target.value)}
                className="w-full rounded border border-input bg-background px-2 py-1.5 text-sm"
              >
                {(cameras.data?.items ?? []).map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="nvr_date">Date</Label>
              <Input
                id="nvr_date"
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nvr_start">Start (UTC)</Label>
              <Input
                id="nvr_start"
                type="time"
                value={start}
                onChange={(e) => setStart(e.target.value)}
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nvr_end">End (UTC)</Label>
              <Input
                id="nvr_end"
                type="time"
                value={end}
                onChange={(e) => setEnd(e.target.value)}
              />
            </div>
          </div>
          {request.isError && (
            <p className="text-xs text-destructive">
              {(request.error as Error).message}
            </p>
          )}
          <Button
            disabled={!cameraId || request.isPending}
            onClick={submit}
            className="bg-[#ff6d00] hover:bg-[#ff6d00]/90"
          >
            {request.isPending ? "Queuing…" : "Request clip"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Clip queue</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {(clips.data?.items?.length ?? 0) === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground">
              No clips queued.
            </p>
          ) : (
            // overflow-x-auto handles mobile: the table's min-width keeps the
            // Actions column visible at full size by scrolling horizontally
            // rather than wrapping. table-fixed locks the colgroup widths so
            // long error strings can't push the Play/Download buttons off-screen.
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-sm" style={{ minWidth: 900 }}>
                <colgroup>
                  <col style={{ width: 140 }} />
                  <col style={{ width: 120 }} />
                  <col style={{ width: 140 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 200 }} />
                  <col style={{ width: 120 }} />
                </colgroup>
                <thead className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 text-left">Camera</th>
                    <th className="px-3 py-2 text-left">Requested</th>
                    <th className="px-3 py-2 text-left">Period (UTC)</th>
                    <th className="px-3 py-2 text-left">Duration</th>
                    <th className="px-3 py-2 text-left">Status</th>
                    <th className="px-3 py-2 text-left">Error</th>
                    <th className="px-3 py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {(clips.data?.items ?? []).map((clip) => {
                    const camName =
                      cameraNameById[clip.camera_id] ?? clip.camera_id.slice(0, 8);
                    const errFull = clip.error_message ?? "";
                    const errVisible =
                      errFull.length > 60 ? `${errFull.slice(0, 60)}…` : errFull;
                    return (
                      <tr key={clip.id} className="border-b last:border-0">
                        <td className="px-3 py-2">
                          <div className="truncate" title={camName}>{camName}</div>
                        </td>
                        <td className="px-3 py-2 text-xs text-muted-foreground">
                          <div className="truncate">
                            {formatDate(clip.created_at)}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">
                          <div className="truncate">
                            {clip.nvr_start_time.slice(11, 16)} →{" "}
                            {clip.nvr_end_time.slice(11, 16)}{" "}
                            <span className="text-muted-foreground">
                              ({clip.nvr_start_time.slice(0, 10)})
                            </span>
                          </div>
                        </td>
                        <td className="px-3 py-2 text-xs">{clip.duration_sec}s</td>
                        <td className="px-3 py-2">
                          <NvrClipStatusBadge status={clip.status} />
                        </td>
                        <td className="px-3 py-2">
                          {errFull ? (
                            <div
                              className="truncate text-[10px] text-destructive"
                              title={errFull}
                            >
                              {errVisible}
                            </div>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {clip.status === "ready" && clip.signed_url ? (
                            <>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setOpenClip(clip)}
                              >
                                Play
                              </Button>
                              <a
                                href={clip.signed_url}
                                target="_blank"
                                rel="noreferrer"
                                className="inline-flex items-center rounded px-2 py-1 text-xs text-muted-foreground hover:bg-muted"
                              >
                                Download
                              </a>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {openClip && (
        <NvrClipModal clip={openClip} onClose={() => setOpenClip(null)} />
      )}
    </div>
  );
}


function NvrClipStatusBadge({ status }: { status: NvrClipStatus }) {
  const variants: Record<
    NvrClipStatus,
    "outline" | "warning" | "online" | "error"
  > = {
    pending: "outline",
    downloading: "warning",
    ready: "online",
    failed: "error",
  };
  return <Badge variant={variants[status]}>{status}</Badge>;
}


function NvrClipModal({
  clip,
  onClose,
}: {
  clip: NvrClip;
  onClose: () => void;
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6">
      <div className="relative w-full max-w-4xl rounded bg-background p-4 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 rounded p-1 hover:bg-muted"
        >
          <XIcon className="h-4 w-4" />
        </button>
        <div className="mb-2">
          <p className="text-sm font-medium">
            NVR clip · {clip.nvr_start_time.slice(0, 16).replace("T", " ")} →{" "}
            {clip.nvr_end_time.slice(11, 16)}
          </p>
          <p className="text-xs text-muted-foreground">
            {clip.duration_sec}s · presigned link valid 1 hour
          </p>
        </div>
        {clip.signed_url && (
          <>
            <video
              src={clip.signed_url}
              controls
              autoPlay
              className="aspect-video w-full rounded bg-black"
            />
            <div className="mt-3 flex justify-end gap-2">
              <a
                href={clip.signed_url}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center rounded border px-3 py-1.5 text-xs hover:bg-muted"
              >
                Download
              </a>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ===========================================================================
// Page shell
// ===========================================================================
export function RecordingsPage() {
  const [tab, setTab] = useState<Tab>("cloud");
  const cameras = useCameras();
  const [cameraId, setCameraId] = useState<string>("");
  const [date, setDate] = useState<string>(todayIso());

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Recordings</h1>
          <p className="text-sm text-muted-foreground">
            Cloud-recorded segments and on-demand NVR retrieval.
          </p>
        </div>
        <div className="flex gap-3">
          <div className="space-y-1">
            <Label htmlFor="filter_camera">Camera</Label>
            <select
              id="filter_camera"
              value={cameraId}
              onChange={(e) => setCameraId(e.target.value)}
              className="rounded border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">All cameras</option>
              {(cameras.data?.items ?? []).map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="filter_date">Date</Label>
            <Input
              id="filter_date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 border-b">
        <TabButton active={tab === "cloud"} onClick={() => setTab("cloud")}>
          <CloudIcon className="mr-1 inline h-3.5 w-3.5" /> Cloud Recordings
        </TabButton>
        <TabButton active={tab === "nvr"} onClick={() => setTab("nvr")}>
          <ServerIcon className="mr-1 inline h-3.5 w-3.5" /> NVR Retrieval
        </TabButton>
        <TabButton active={tab === "multi"} onClick={() => setTab("multi")}>
          <GridIcon className="mr-1 inline h-3.5 w-3.5" /> Multi-Camera Playback
        </TabButton>
        <TabButton active={tab === "schedule"} onClick={() => setTab("schedule")}>
          <CalendarRangeIcon className="mr-1 inline h-3.5 w-3.5" /> Schedule
        </TabButton>
        <TabButton active={tab === "compliance"} onClick={() => setTab("compliance")}>
          <CheckCircle2Icon className="mr-1 inline h-3.5 w-3.5" /> Compliance
        </TabButton>
      </div>

      {tab === "cloud" && <CloudTab cameraId={cameraId} date={date} />}
      {tab === "nvr" && <NvrTab defaultCameraId={cameraId} />}
      {tab === "multi" && <MultiCameraPlayback />}
      {tab === "schedule" && <RecordingScheduleEditor />}
      {tab === "compliance" && <ComplianceDashboard />}
    </div>
  );
}


function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`-mb-px border-b-2 px-3 py-2 text-sm transition ${
        active
          ? "border-[#ff6d00] font-medium text-foreground"
          : "border-transparent text-muted-foreground hover:text-foreground"
      }`}
    >
      {children}
    </button>
  );
}
